import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/store/root-state';
import type { ISupportMessage, ISupportTicket } from '@/domain/entities/support';
import { logoutUser } from '@/features/auth/authThunks';
import { SUPPORT_QUEUE_STATUSES, type SupportQueueStatus, type SupportState } from './supportTypes';
import {
  fetchSupportTicketThread,
  fetchSupportTicketsByProperty,
  fetchSupportTicketsByStatus,
  replyToSupportTicket,
  updateSupportTicketStatus,
} from './supportThunks';

function buildStatusMap<T>(factory: () => T): Record<SupportQueueStatus, T> {
  return {
    open: factory(),
    closed: factory(),
  };
}

function mergeUniqueTickets(existing: ISupportTicket[], incoming: ISupportTicket[]) {
  const byId = new Map(existing.map((ticket) => [ticket.id, ticket]));
  for (const ticket of incoming) {
    byId.set(ticket.id, ticket);
  }
  return Array.from(byId.values());
}

function isSupportMessage(value: unknown): value is ISupportMessage {
  if (!value || typeof value !== 'object') return false;

  const candidate = value as Partial<ISupportMessage>;
  return (
    typeof candidate.id === 'string' &&
    candidate.id.trim().length > 0 &&
    typeof candidate.ticketID === 'string' &&
    candidate.ticketID.trim().length > 0 &&
    (candidate.propertyID === undefined ||
      (typeof candidate.propertyID === 'string' && candidate.propertyID.trim().length > 0)) &&
    typeof candidate.authorType === 'string' &&
    typeof candidate.authorID === 'string' &&
    candidate.authorID.trim().length > 0 &&
    typeof candidate.body === 'string'
  );
}

function sanitizeSupportMessages(messages: unknown[]): ISupportMessage[] {
  return messages.filter(isSupportMessage);
}

function upsertThreadMessage(messages: ISupportMessage[], next: ISupportMessage) {
  const index = messages.findIndex((message) => message.id === next.id);
  if (index >= 0) {
    const copy = messages.slice();
    copy[index] = next;
    return copy;
  }
  return [...messages, next];
}

function removeTicketFromAllBuckets(state: SupportState, ticketID: string) {
  for (const status of SUPPORT_QUEUE_STATUSES) {
    state.itemsByStatus[status] = state.itemsByStatus[status].filter((ticket) => ticket.id !== ticketID);
  }
}

function upsertTicketIntoBucket(state: SupportState, ticket: ISupportTicket) {
  removeTicketFromAllBuckets(state, ticket.id);
  state.itemsByStatus[ticket.status] = [ticket, ...state.itemsByStatus[ticket.status]];
}

const initialState: SupportState = {
  activeQueueStatus: 'open',
  itemsByStatus: buildStatusMap(() => []),
  hasFetchedByStatus: buildStatusMap(() => false),
  isListingByStatus: buildStatusMap(() => false),
  errorByStatus: buildStatusMap(() => null),
  nextTokenByStatus: buildStatusMap(() => null),
  totalByStatus: buildStatusMap(() => 0),

  selectedTicket: null,
  selectedMessages: [],
  isLoadingThread: false,
  threadError: null,

  isReplying: false,
  replyError: null,

  isUpdatingStatus: false,
  updateStatusError: null,
};

const supportSlice = createSlice({
  name: 'support',
  initialState,
  reducers: {
    setActiveSupportQueueStatus: (state, action: PayloadAction<SupportQueueStatus>) => {
      state.activeQueueStatus = action.payload;
    },
    clearSelectedSupportThread: (state) => {
      state.selectedTicket = null;
      state.selectedMessages = [];
      state.threadError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSupportTicketsByStatus.pending, (state, action) => {
        const status = action.meta.arg.status;
        state.isListingByStatus[status] = true;
        state.errorByStatus[status] = null;
      })
      .addCase(fetchSupportTicketsByStatus.fulfilled, (state, action) => {
        const { status, nextToken } = action.meta.arg;
        const isAppend = Boolean(nextToken);

        state.isListingByStatus[status] = false;
        state.hasFetchedByStatus[status] = true;
        state.errorByStatus[status] = null;
        state.totalByStatus[status] = action.payload.total;
        state.nextTokenByStatus[status] = action.payload.nextToken ?? null;
        state.itemsByStatus[status] = isAppend
          ? mergeUniqueTickets(state.itemsByStatus[status], action.payload.items)
          : action.payload.items;
      })
      .addCase(fetchSupportTicketsByStatus.rejected, (state, action) => {
        const status = action.meta.arg.status;
        state.isListingByStatus[status] = false;
        state.hasFetchedByStatus[status] = true;
        state.errorByStatus[status] =
          action.payload?.message || action.error.message || 'Failed to fetch support tickets';
      })
      .addCase(fetchSupportTicketsByProperty.pending, (state) => {
        state.threadError = null;
      })
      .addCase(fetchSupportTicketThread.pending, (state) => {
        state.isLoadingThread = true;
        state.threadError = null;
      })
      .addCase(fetchSupportTicketThread.fulfilled, (state, action) => {
        state.isLoadingThread = false;
        state.threadError = null;
        state.selectedTicket = action.payload.ticket;
        state.selectedMessages = sanitizeSupportMessages(action.payload.messages);
        upsertTicketIntoBucket(state, action.payload.ticket);
      })
      .addCase(fetchSupportTicketThread.rejected, (state, action) => {
        state.isLoadingThread = false;
        state.threadError = action.payload?.message || action.error.message || 'Failed to load support thread';
      })
      .addCase(replyToSupportTicket.pending, (state) => {
        state.isReplying = true;
        state.replyError = null;
      })
      .addCase(replyToSupportTicket.fulfilled, (state, action) => {
        state.isReplying = false;
        state.replyError = null;
        if (!isSupportMessage(action.payload)) {
          return;
        }

        state.selectedMessages = upsertThreadMessage(state.selectedMessages, action.payload);

        if (!state.selectedTicket || state.selectedTicket.id !== action.payload.ticketID) {
          return;
        }

        const updatedTicket: ISupportTicket = {
          ...state.selectedTicket,
          status: 'open',
          awaiting: 'user',
          closedReason: null,
          updatedAt: action.payload.updatedAt ?? state.selectedTicket.updatedAt,
          lastMessageAt: action.payload.createdAt ?? state.selectedTicket.lastMessageAt,
          latestMessagePreview: action.payload.body,
          lastMessageAuthorType: action.payload.authorType,
          messageCount: (state.selectedTicket.messageCount ?? 0) + 1,
          firstResponseAt: state.selectedTicket.firstResponseAt ?? action.payload.createdAt ?? null,
        };

        state.selectedTicket = updatedTicket;
        upsertTicketIntoBucket(state, updatedTicket);
      })
      .addCase(replyToSupportTicket.rejected, (state, action) => {
        state.isReplying = false;
        state.replyError = action.payload?.message || action.error.message || 'Failed to reply to support ticket';
      })
      .addCase(updateSupportTicketStatus.pending, (state) => {
        state.isUpdatingStatus = true;
        state.updateStatusError = null;
      })
      .addCase(updateSupportTicketStatus.fulfilled, (state, action) => {
        state.isUpdatingStatus = false;
        state.updateStatusError = null;
        state.selectedTicket = action.payload.ticket;
        if (isSupportMessage(action.payload.systemMessage)) {
          state.selectedMessages = upsertThreadMessage(state.selectedMessages, action.payload.systemMessage);
        }
        upsertTicketIntoBucket(state, action.payload.ticket);
      })
      .addCase(updateSupportTicketStatus.rejected, (state, action) => {
        state.isUpdatingStatus = false;
        state.updateStatusError =
          action.payload?.message || action.error.message || 'Failed to update support ticket status';
      })
      .addCase(logoutUser.fulfilled, () => ({ ...initialState }));
  },
});

export const { setActiveSupportQueueStatus, clearSelectedSupportThread } = supportSlice.actions;
export default supportSlice.reducer;

const selectSupportState = (state: RootState) => state.support;

export const selectActiveSupportQueueStatus = createSelector(selectSupportState, (state) => state.activeQueueStatus);
export const selectSupportSelectedTicket = createSelector(selectSupportState, (state) => state.selectedTicket);
export const selectSupportSelectedMessages = createSelector(selectSupportState, (state) => state.selectedMessages);
export const selectSupportThreadLoading = createSelector(selectSupportState, (state) => state.isLoadingThread);
export const selectSupportThreadError = createSelector(selectSupportState, (state) => state.threadError);
export const selectSupportReplying = createSelector(selectSupportState, (state) => state.isReplying);
export const selectSupportReplyError = createSelector(selectSupportState, (state) => state.replyError);
export const selectSupportUpdatingStatus = createSelector(selectSupportState, (state) => state.isUpdatingStatus);
export const selectSupportUpdateStatusError = createSelector(selectSupportState, (state) => state.updateStatusError);

export const selectSupportTicketsByStatus = (status: SupportQueueStatus) =>
  createSelector(selectSupportState, (state) => state.itemsByStatus[status]);

export const selectSupportTicketsLoadingByStatus = (status: SupportQueueStatus) =>
  createSelector(selectSupportState, (state) => state.isListingByStatus[status]);

export const selectSupportTicketsErrorByStatus = (status: SupportQueueStatus) =>
  createSelector(selectSupportState, (state) => state.errorByStatus[status]);

export const selectSupportTicketsHasFetchedByStatus = (status: SupportQueueStatus) =>
  createSelector(selectSupportState, (state) => state.hasFetchedByStatus[status]);

export const selectSupportTicketsNextTokenByStatus = (status: SupportQueueStatus) =>
  createSelector(selectSupportState, (state) => state.nextTokenByStatus[status]);

export const selectSupportTicketsTotalByStatus = (status: SupportQueueStatus) =>
  createSelector(selectSupportState, (state) => state.totalByStatus[status]);
